import { FileText, Eye, LayoutTemplate, Layers } from "lucide-react";
import { useProposalStore } from "../../store/useProposalStore";
import { useAuthStore } from "../../store/useAuthStore";


const BarList = ({
  title,
  rows,
  tone = "bg-primary",
}: {
  title: string;
  rows: { label: string; count: number }[];
  tone?: string;
}) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-4">
      <h4 className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant mb-3">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-[12px] text-outline py-4 text-center">No data yet.</p>
      ) : (
        <div className="space-y-2.5">
          {rows.slice(0, 6).map((r) => (
            <div key={r.label}>
              <div className="flex justify-between text-[12px] mb-1">
                <span className="font-semibold text-on-surface truncate max-w-[70%] capitalize">{r.label}</span>
                <span className="font-bold text-on-surface-variant">{r.count}</span>
              </div>
              <div className="h-2 rounded-full bg-surface-container-high overflow-hidden">
                <div className={`h-full ${tone} rounded-full transition-all`} style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Kpi = ({ icon: Icon, label, value, tone }: { icon: any; label: string; value: string | number; tone: string }) => (
  <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-4 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-lg grid place-items-center ${tone}`}>
      <Icon size={18} />
    </div>
    <div className="min-w-0">
      <div className="text-xl font-extrabold text-on-surface leading-none">{value}</div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant mt-1 truncate">{label}</div>
    </div>
  </div>
);

const ProposalAnalytics = () => {
  const { stats, isFetchingStats } = useProposalStore();
  const { authUser } = useAuthStore();
  const isBD = authUser?.role === "Business Developer";

  if (isFetchingStats && !stats) {
    return <div className="p-4 text-center text-[13px] text-on-surface-variant">Loading analytics…</div>;
  }
  if (!stats) return null;

  return (
    <div className="flex flex-col gap-4">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={FileText} label="Total Proposals" value={stats.total} tone="bg-blue-50 text-blue-700" />
        <Kpi icon={Eye} label="Total Views" value={stats.totalViews} tone="bg-emerald-50 text-emerald-700" />
        <Kpi icon={LayoutTemplate} label="Most-used Template" value={stats.mostUsedTemplate} tone="bg-violet-50 text-violet-700" />
        <Kpi icon={Layers} label="Top Category" value={stats.topCategory} tone="bg-amber-50 text-amber-700" />
      </div>

      {/* Comparison bars */}
      <div className={`grid grid-cols-1 ${isBD ? "md:grid-cols-2" : "md:grid-cols-3"} gap-3`}>
        <BarList
          title="By Category"
          rows={stats.byCategory.map((c) => ({ label: c.label, count: c.count }))}
          tone="bg-green-600"
        />
        <BarList
          title="Template Usage"
          rows={stats.byTemplate.map((t) => ({ label: t.label, count: t.count }))}
          tone="bg-violet-600"
        />
        {/* By-BD comparison is admin-only (a BD only ever sees themselves). */}
        {!isBD && (
          <BarList
            title="By Business Developer"
            rows={stats.byBd.map((b) => ({ label: b.label, count: b.count }))}
            tone="bg-blue-500"
          />
        )}
      </div>

      {/* Activity over time (last 6 months) */}
      <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-4">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant mb-3">Proposals Over Time (6 months)</h4>
        <div className="flex items-end gap-2 h-24">
          {stats.overTime.map((m) => {
            const max = Math.max(1, ...stats.overTime.map((x) => x.count));
            return (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex items-end justify-center flex-1">
                  <div className="w-full bg-primary/80 rounded-t-md transition-all" style={{ height: `${(m.count / max) * 100}%`, minHeight: m.count ? 4 : 0 }} title={`${m.count} proposals`} />
                </div>
                <span className="text-[10px] text-on-surface-variant">{m.month.slice(5)}</span>
                <span className="text-[10px] font-bold text-on-surface">{m.count}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProposalAnalytics;
